import _ from 'lodash';

import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import {
  CustomIcon,
  FilePdfViewer,
  RemoveDocumentModal,
  SaveDocumentModal,
  useAccessState,
  useContainerDimensions
} from 'modules';
import {
  ReactNode,
  useRef,
  useState
} from 'react';
import QRCode from 'react-qr-code';
import {
  useNavigate,
  useParams
} from 'react-router-dom';
import { theme } from 'theme';
import * as ds from './dataService';



type IDataKeys = keyof ds.IData;

type IFieldName = Exclude<IDataKeys, 'id' | 'apiFileId' | 'url'>;


const fieldNames: IFieldName[] = [
  'fileSize',
  'createdTimestamp',
  'updatedTimestamp',
  'fileType',
  'uploadedBy'

];

const fieldName2Label: Record<IFieldName, string> = {
  fileSize: 'File Size',
  createdTimestamp: 'Created At',
  updatedTimestamp: 'Updated At',
  fileType: 'File Type',
  title: 'Title',
  uploadedBy: 'Uploaded By'
};

const fieldName2Icon: Record<IFieldName, () => ReactNode> = {
  fileSize: () => <CustomIcon.large.FC icon='DOC' stroke={theme.palette.primary.main} />,
  createdTimestamp: () => <CustomIcon.large.FC icon='CALENDARALT' stroke={theme.palette.primary.main} />,
  updatedTimestamp: () => <CustomIcon.large.FC icon='CALENDARALT' stroke={theme.palette.primary.main} />,
  title: () => <CustomIcon.large.FC icon='DOC' stroke={theme.palette.primary.main} />,
  fileType: () => <CustomIcon.large.FC icon='FILE' stroke={theme.palette.primary.main} />,
  uploadedBy: () => <CustomIcon.large.FC icon='CREATOR' stroke={theme.palette.primary.main} />
};



export type FileDetailSectionProps = {};

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
export const FC = (props: FileDetailSectionProps) => {
  const params = useParams();
  const ref = useRef(null);
  const navigate = useNavigate();
  const { data } = ds.useQuery(params.documentId ?? '');
  const { width } = useContainerDimensions(ref);
  const access = useAccessState('Document');

  const [
    isVisible,
    setIsVisible
  ] = useState(false);

  const [
    isRemoveVisible,
    setIsRemoveVisible
  ] = useState(false);



  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };



  return (
    <>
      {isVisible && (
        <SaveDocumentModal.FC
          id={params.documentId ?? ''}
          handleClose={() => setIsVisible(() => false)}
          isVisible={isVisible}
        />
      )} {
        isRemoveVisible && (
          <RemoveDocumentModal.FC
            id={params.documentId ?? ''}
            isVisible={isRemoveVisible}
            handleClose={() => setIsRemoveVisible(() => false)}
          />
        )
      }
      <Box
        ref={ref}
        display='flex'
        flexDirection='row'
        columnGap='1rem'
      >
        <Box
          paddingTop='1rem'
          flex='3 1 0'
          minHeight='calc(100vh - 2.5rem)'
        >
          {data.fileType === 'application/pdf'
            ? (
              <FilePdfViewer.FC
                width={width.mul(0.6).round(0)}
                apiFileId={data.apiFileId}
              />
            )
            : data.fileType.includes('image')
              ? (
                <Box
                  height='100%'
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                >
                  <Box>
                    <Box
                      borderRadius='.5rem'
                      maxWidth='100%'
                      maxHeight='100%'
                      component='img'
                      src={data.url}
                    />
                  </Box>
                </Box>
              )
              : (
                <Box
                  height='100%'
                  bgcolor='lightgray'
                  display='flex'
                  justifyContent='center'
                >
                  <Typography
                    margin='auto'
                    variant='modalSubTitle'
                  >
                    No Preview available
                  </Typography>
                </Box>
              )}
        </Box>
        <Box
          width={`${width.mul(0.25).round(0)
            .toNumber()}px`}
          display='flex'
          flex='1 1 0'
          flexDirection='column'
          rowGap='2rem'
          padding='2.5rem'
          maxHeight='100vh'
          bgcolor='white'
          marginTop='-1.25rem'
        >
          {/* HEADER */}
          <Stack
            direction='row'
            overflow='clip'
            alignItems='center'
            justifyContent='flex-start'
            columnGap='1rem'
          >
            <IconButton
              onClick={() => {
                navigate('..');
              }}
              sx={{
                width: '2.25rem',
                height: '2.25rem',
                padding: '0.625rem',
                borderRadius: '.5rem',
                background: theme.palette.secondary.main
              }}
            >
              <CustomIcon.medium.FC
                icon='CHEVRONLEFT'
                stroke={theme.palette.secondary.darker}
              />
            </IconButton>
            <Typography

              textOverflow='ellipsis'

              variant='modalTitle'
              sx={{
                wordBreak: 'break-word'
              }}
            >
              {data.title}
            </Typography>
            <IconButton
              onClick={() => setIsVisible(() => true)}
            >
              <CustomIcon.small.FC
                icon='PENCIL'
                stroke={theme.palette.primary.main}
              />
            </IconButton>
          </Stack>

          {/* FIELDS */}
          <Box
            display='flex'
            flexDirection='column'
            overflow='clip'
            rowGap='1rem'
          >
            {fieldNames.map((node, i) => {
              const Icon = fieldName2Icon[node];
              const label = fieldName2Label[node];
              return (
                <Stack
                  key={i}
                  direction='row'
                  columnGap='.5rem'
                  alignItems='center'
                >
                  <Icon />

                  <Typography
                    variant='formLabel'
                  >
                    {label}
                  </Typography>
                  <Typography
                    variant='formInput'
                  >
                    {_.isString(data[node])
                      ? data[node]
                      : data[node].toFormat('DDDD')}
                  </Typography>

                </Stack>
              );
            })}
          </Box>

          {/* ACTION BUTTONS */}
          <Stack
            direction='column'
            rowGap='.5rem'
          >
            <Button
              variant='contained'
              color='primary'
              startIcon={(
                <CustomIcon.large.FC
                  icon='DOWLOAD'
                  stroke={theme.palette.primary.contrastText}
                />
              )}
              onClick={() => handleDownload(data.url)}
            >
              Download
            </Button>
            <Button
              variant='outlined'
              disabled={!access.delete}
              startIcon={(
                <CustomIcon.large.FC icon='DELETE' stroke={theme.palette.error.main} />
              )}
              color='error'
              sx={{
                bgcolor: theme.palette.error.light,
                color: theme.palette.error.main
              }}
              onClick={() => {
                setIsRemoveVisible(() => true);
              }}
            >
              Remove
            </Button>
          </Stack>

          {/* QRDOWNLOAD */}
          <Stack
            direction='column'
            padding='2.5rem'
            rowGap='1.5rem'
          >
            <Box
              margin='auto'
            >
              <QRCode
                value={data.url}
              />
            </Box>
            <Typography
              variant='modalTitle'
              textAlign='center'
              sx={{
                wordBreak: 'break-word'
              }}
            >
              Scan QR code to download
            </Typography>

            <Typography
              variant='body1'
              textAlign='center'
              sx={{
                marginTop: '-1rem',
                wordBreak: 'break-word'
              }}
            >
              This code will let you download plan on any device.
            </Typography>
          </Stack>

        </Box>
      </Box>

    </>
  );
};


